import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export async function api<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const res = await fetch(path, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  let data: { error?: { message?: string; code?: string; fields?: Record<string, string> } } | null = null;
  try {
    data = await res.json();
  } catch {
    throw new ApiError(
      res.ok ? 'Invalid server response' : `Server error (${res.status}). Is the dev server running?`,
      'PARSE_ERROR',
      res.status,
    );
  }

  if (!res.ok) {
    const message =
      data?.error?.message === 'no_such_route'
        ? 'Could not reach the API. Restart the dev server with pnpm dev.'
        : (data?.error?.message ?? 'Request failed');
    throw new ApiError(message, data?.error?.code, res.status, data?.error?.fields);
  }
  return data as T;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public code?: string,
    public status?: number,
    public fields?: Record<string, string>,
  ) {
    super(message);
  }
}

export function formatCurrency(amount: number, currency = 'USD') {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
}

export function formatDate(date: string) {
  return new Date(date + 'T12:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export type PlanTierId = 'lite' | 'starter' | 'growth' | 'pro';

export interface PlanInfo {
  id: PlanTierId;
  name: string;
  tagline: string;
  priceMonthly: number;
  recommended: boolean;
  limits: { activeAgreements: number; customers: number; emailsPerMonth: number; customTemplates: number };
  features: string[];
  priceId: string | null;
}

export interface PlansResponse {
  plans: PlanInfo[];
  trialDays: number;
  billingProvider?: 'paddle' | 'dodo' | 'stripe';
  paddleClientToken?: string | null;
  paddleEnvironment?: 'sandbox' | 'production' | null;
  dodoEnvironment?: 'test' | 'live' | null;
}
